import React, { ReactElement, useCallback, useContext, useEffect, useState } from 'react'
import { LanguageContext, ReactState } from '../../Common/Contexts'
import LanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { DisplaySettings as DisplaySettingsLanguage } from '../../../../i18n'

import Heading from '../../Common/Components/Heading'
import FormRow from '../../Common/Components/FormRow'
import Fieldset from '../../Common/Components/Fieldset'
import { Select, SelectOptions } from '../../Common/Components/Selects'
import Checkbox from '../../Common/Components/Checkbox'
import TextInput from '../../Common/Components/TextInput'

import TilePreview, { TileBackgroundColors, TilePreviewProps } from './TilePreview'
import { StatisticsConfiguration, TileDisplayAsValues } from './Types'
import {
    STATISTICS_UNIT_TYPE_CURRENCY,
    STATISTICS_UNIT_TYPE_DATE,
    STATISTICS_UNIT_TYPE_DATETIME,
    STATISTICS_UNIT_TYPE_DURATION,
    STATISTICS_UNIT_TYPE_FLOATORDOUBLE,
    STATISTICS_UNIT_TYPE_ICON,
    STATISTICS_UNIT_TYPE_INTEGERORLONG,
    STATISTICS_UNIT_TYPE_PERCENTAGE,
    STATISTICS_UNIT_TYPE_TEXT,
    STATISTICS_UNIT_TYPE_TEXT_TRUNCATABLE,
    STATISTICS_UNIT_TYPE_TIME,
    STATISTICS_UNIT_TYPE_UNDEFINED
} from './Constants'
import {
    convertDateToDateString,
    convertDateToDurationDateTimeString,
    convertDateToDurationDateTimeString12HourFormat,
    convertDateToDurationTimeString,
    convertDateToDurationTimeString12HourFormat
} from '../../Common/Utils/TimeFormats'
import { HeadingLevel } from '../../Common/Components/Heading/Heading'
import { EMPTY_STRING } from '../../../NonReact/constants/Common'

export type DisplaySettingsProps = {
    selectedStatisticsConfiguration: StatisticsConfiguration | undefined
    updateSelectedStatisticsConfiguration: (updatedStatisticsConfiguration: StatisticsConfiguration) => void
    currencyFormatter: CDecimalFormatter
}

const getDisplayAsOptions = (language: LanguageHandler): SelectOptions<number> => [
    {
        label: `${language?.get(DisplaySettingsLanguage.SimpleTextValue)}`,
        value: TileDisplayAsValues.SIMPLE_TEXT_VALUE
    },
    { label: `${language?.get(DisplaySettingsLanguage.ProgressRing)}`, value: TileDisplayAsValues.PROGRESS_RING },
    { label: `${language?.get(DisplaySettingsLanguage.GradatedDial)}`, value: TileDisplayAsValues.GRADATED_DIAL },
    { label: `${language?.get(DisplaySettingsLanguage.GlideSlope)}`, value: TileDisplayAsValues.GLIDE_SLOPE },
    { label: `${language?.get(DisplaySettingsLanguage.Bullseye)}`, value: TileDisplayAsValues.BULLSEYE },
    { label: `${language?.get(DisplaySettingsLanguage.BarGauge)}`, value: TileDisplayAsValues.BAR_GAUGE },
    { label: `${language?.get(DisplaySettingsLanguage.ConeGauge)}`, value: TileDisplayAsValues.CONE_GAUGE },
    {
        label: `${language?.get(DisplaySettingsLanguage.DashboardSpeedometerBlack)}`,
        value: TileDisplayAsValues.DASHBOARD_SPEEDOMETER_BLACK
    },
    {
        label: `${language?.get(DisplaySettingsLanguage.DashboardSpeedometerAmber)}`,
        value: TileDisplayAsValues.DASHBOARD_SPEEDOMETER_AMBER
    },
    {
        label: `${language?.get(DisplaySettingsLanguage.DashboardTemperatureGauge)}`,
        value: TileDisplayAsValues.DASHBOARD_TEMPERATURE_GAUGE
    },
    {
        label: `${language?.get(DisplaySettingsLanguage.DashboardFuelGauge)}`,
        value: TileDisplayAsValues.DASHBOARD_FUEL_GAUGE
    },
    {
        label: `${language?.get(DisplaySettingsLanguage.DashboardPowerGauge)}`,
        value: TileDisplayAsValues.DASHBOARD_POWER_GAUGE
    },
    {
        label: `${language?.get(DisplaySettingsLanguage.DashboardColourGauge)}`,
        value: TileDisplayAsValues.DASHBOARD_COLOUR_GAUGE
    }
]

const toColorOptionRenderElement = (cssColorValue: string, label: string): ReactElement => (
    <div>
        <span className='color-square' style={{ backgroundColor: cssColorValue }}></span>
        {label}
    </div>
)

const getColorOptions = (language: LanguageHandler): SelectOptions<number> => [
    {
        renderOptionElement: toColorOptionRenderElement(
            TileBackgroundColors.BLACK,
            language?.get(DisplaySettingsLanguage.Black)
        ),
        label: language?.get(DisplaySettingsLanguage.Black),
        value: 10
    },
    {
        renderOptionElement: toColorOptionRenderElement(
            TileBackgroundColors.WHITE,
            language?.get(DisplaySettingsLanguage.White)
        ),
        label: language?.get(DisplaySettingsLanguage.White),
        value: 20
    },
    {
        renderOptionElement: toColorOptionRenderElement(
            TileBackgroundColors.BLANC,
            language?.get(DisplaySettingsLanguage.Blanc)
        ),
        label: language?.get(DisplaySettingsLanguage.Blanc),
        value: 23
    },
    {
        renderOptionElement: toColorOptionRenderElement(
            TileBackgroundColors.SMOKE,
            language?.get(DisplaySettingsLanguage.Smoke)
        ),
        label: language?.get(DisplaySettingsLanguage.Smoke),
        value: 33
    },
    {
        renderOptionElement: toColorOptionRenderElement(
            TileBackgroundColors.GREY,
            language?.get(DisplaySettingsLanguage.Grey)
        ),
        label: language?.get(DisplaySettingsLanguage.Grey),
        value: 30
    },
    {
        renderOptionElement: toColorOptionRenderElement(
            TileBackgroundColors.BATTLESHIP,
            language?.get(DisplaySettingsLanguage.Battleship)
        ),
        label: language?.get(DisplaySettingsLanguage.Battleship),
        value: 37
    },
    {
        renderOptionElement: toColorOptionRenderElement(
            TileBackgroundColors.AQUA,
            language?.get(DisplaySettingsLanguage.Aqua)
        ),
        label: language?.get(DisplaySettingsLanguage.Aqua),
        value: 43
    },
    {
        renderOptionElement: toColorOptionRenderElement(
            TileBackgroundColors.VIVID_SKY,
            language?.get(DisplaySettingsLanguage.VividSky)
        ),
        label: language?.get(DisplaySettingsLanguage.VividSky),
        value: 134
    },
    {
        renderOptionElement: toColorOptionRenderElement(
            TileBackgroundColors.BLUE,
            language?.get(DisplaySettingsLanguage.Blue)
        ),
        label: language?.get(DisplaySettingsLanguage.Blue),
        value: 40
    },
    {
        renderOptionElement: toColorOptionRenderElement(
            TileBackgroundColors.ELECTRIC_INDIGO,
            language?.get(DisplaySettingsLanguage.ElectricIndigo)
        ),
        label: language?.get(DisplaySettingsLanguage.ElectricIndigo),
        value: 132
    },
    {
        renderOptionElement: toColorOptionRenderElement(
            TileBackgroundColors.WATERS,
            language?.get(DisplaySettingsLanguage.Waters)
        ),
        label: language?.get(DisplaySettingsLanguage.Waters),
        value: 106
    },
    {
        renderOptionElement: toColorOptionRenderElement(
            TileBackgroundColors.SKY,
            language?.get(DisplaySettingsLanguage.Sky)
        ),
        label: language?.get(DisplaySettingsLanguage.Sky),
        value: 107
    },
    {
        renderOptionElement: toColorOptionRenderElement(
            TileBackgroundColors.AKIXI,
            language?.get(DisplaySettingsLanguage.Akixi)
        ),
        label: language?.get(DisplaySettingsLanguage.Akixi),
        value: 108
    },
    {
        renderOptionElement: toColorOptionRenderElement(
            TileBackgroundColors.MIDNIGHT,
            language?.get(DisplaySettingsLanguage.Midnight)
        ),
        label: language?.get(DisplaySettingsLanguage.Midnight),
        value: 47
    },
    {
        renderOptionElement: toColorOptionRenderElement(
            TileBackgroundColors.OXFORD_BLUE,
            language?.get(DisplaySettingsLanguage.OxfordBlue)
        ),
        label: language?.get(DisplaySettingsLanguage.OxfordBlue),
        value: 131
    },
    {
        renderOptionElement: toColorOptionRenderElement(
            TileBackgroundColors.PISTACHIO,
            language?.get(DisplaySettingsLanguage.Pistachio)
        ),
        label: language?.get(DisplaySettingsLanguage.Pistachio),
        value: 73
    },
    {
        renderOptionElement: toColorOptionRenderElement(
            TileBackgroundColors.AQUAMARINE,
            language?.get(DisplaySettingsLanguage.Aquamarine)
        ),
        label: language?.get(DisplaySettingsLanguage.Aquamarine),
        value: 133
    },
    {
        renderOptionElement: toColorOptionRenderElement(
            TileBackgroundColors.GREEN,
            language?.get(DisplaySettingsLanguage.Green)
        ),
        label: language?.get(DisplaySettingsLanguage.Green),
        value: 70
    },
    {
        renderOptionElement: toColorOptionRenderElement(
            TileBackgroundColors.FOREST,
            language?.get(DisplaySettingsLanguage.Forest)
        ),
        label: language?.get(DisplaySettingsLanguage.Forest),
        value: 77
    },
    {
        renderOptionElement: toColorOptionRenderElement(
            TileBackgroundColors.SALMON,
            language?.get(DisplaySettingsLanguage.Salmon)
        ),
        label: language?.get(DisplaySettingsLanguage.Salmon),
        value: 63
    },
    {
        renderOptionElement: toColorOptionRenderElement(
            TileBackgroundColors.RED,
            language?.get(DisplaySettingsLanguage.Red)
        ),
        label: language?.get(DisplaySettingsLanguage.Red),
        value: 60
    },
    {
        renderOptionElement: toColorOptionRenderElement(
            TileBackgroundColors.ROSITA,
            language?.get(DisplaySettingsLanguage.Rosita)
        ),
        label: language?.get(DisplaySettingsLanguage.Rosita),
        value: 67
    },
    {
        renderOptionElement: toColorOptionRenderElement(
            TileBackgroundColors.PEACH,
            language?.get(DisplaySettingsLanguage.Peach)
        ),
        label: language?.get(DisplaySettingsLanguage.Peach),
        value: 116
    },
    {
        renderOptionElement: toColorOptionRenderElement(
            TileBackgroundColors.ORANGE,
            language?.get(DisplaySettingsLanguage.Orange)
        ),
        label: language?.get(DisplaySettingsLanguage.Orange),
        value: 117
    },
    {
        renderOptionElement: toColorOptionRenderElement(
            TileBackgroundColors.CLEMENTINE,
            language?.get(DisplaySettingsLanguage.Clementine)
        ),
        label: language?.get(DisplaySettingsLanguage.Clementine),
        value: 118
    },
    {
        renderOptionElement: toColorOptionRenderElement(
            TileBackgroundColors.AMARELO,
            language?.get(DisplaySettingsLanguage.Amarelo)
        ),
        label: language?.get(DisplaySettingsLanguage.Amarelo),
        value: 53
    },
    {
        renderOptionElement: toColorOptionRenderElement(
            TileBackgroundColors.YELLOW,
            language?.get(DisplaySettingsLanguage.Yellow)
        ),
        label: language?.get(DisplaySettingsLanguage.Yellow),
        value: 50
    },
    {
        renderOptionElement: toColorOptionRenderElement(
            TileBackgroundColors.SUNFLOWER,
            language?.get(DisplaySettingsLanguage.Sunflower)
        ),
        label: language?.get(DisplaySettingsLanguage.Sunflower),
        value: 57
    },
    {
        renderOptionElement: toColorOptionRenderElement(
            TileBackgroundColors.LILAC,
            language?.get(DisplaySettingsLanguage.Lilac)
        ),
        label: language?.get(DisplaySettingsLanguage.Lilac),
        value: 122
    },
    {
        renderOptionElement: toColorOptionRenderElement(
            TileBackgroundColors.PURPLE,
            language?.get(DisplaySettingsLanguage.Purple)
        ),
        label: language?.get(DisplaySettingsLanguage.Purple),
        value: 123
    },
    {
        renderOptionElement: toColorOptionRenderElement(
            TileBackgroundColors.REGAL,
            language?.get(DisplaySettingsLanguage.Regal)
        ),
        label: language?.get(DisplaySettingsLanguage.Regal),
        value: 124
    },
    {
        renderOptionElement: toColorOptionRenderElement(
            TileBackgroundColors.BLUSH,
            language?.get(DisplaySettingsLanguage.Blush)
        ),
        label: language?.get(DisplaySettingsLanguage.Blush),
        value: 125
    },
    {
        renderOptionElement: toColorOptionRenderElement(
            TileBackgroundColors.PINK,
            language?.get(DisplaySettingsLanguage.Pink)
        ),
        label: language?.get(DisplaySettingsLanguage.Pink),
        value: 126
    },
    {
        renderOptionElement: toColorOptionRenderElement(
            TileBackgroundColors.FUCHSIA,
            language?.get(DisplaySettingsLanguage.Fuchsia)
        ),
        label: language?.get(DisplaySettingsLanguage.Fuchsia),
        value: 127
    },
    {
        renderOptionElement: toColorOptionRenderElement(
            TileBackgroundColors.CARIBBEAN,
            language?.get(DisplaySettingsLanguage.Caribbean)
        ),
        label: language?.get(DisplaySettingsLanguage.Caribbean),
        value: 128
    },
    {
        renderOptionElement: toColorOptionRenderElement(
            TileBackgroundColors.TEAL,
            language?.get(DisplaySettingsLanguage.Teal)
        ),
        label: language?.get(DisplaySettingsLanguage.Teal),
        value: 129
    },
    {
        renderOptionElement: toColorOptionRenderElement(
            TileBackgroundColors.PEACOCK,
            language?.get(DisplaySettingsLanguage.Peacock)
        ),
        label: language?.get(DisplaySettingsLanguage.Peacock),
        value: 130
    }
]

/**
 *
 * @param selectedStatisticsConfiguration Statistics field for which to render display setting options.
 * @param updateSelectedStatisticsConfiguration Setter for respective property.
 * @param currencyFormatter Currency Formatter currently used to format financial values.
 * @constructor
 */
const DisplaySettings: React.FC<DisplaySettingsProps> = ({
    selectedStatisticsConfiguration,
    updateSelectedStatisticsConfiguration,
    currencyFormatter
}: DisplaySettingsProps): ReactElement => {
    const language: LanguageHandler = useContext(LanguageContext) as LanguageHandler
    language.loadBundle('DisplaySettings')

    const displayAsOptions: SelectOptions<number> = getDisplayAsOptions(language)
    const colorOptions: SelectOptions<number> = getColorOptions(language)
    const isStatisticsSelected: boolean = selectedStatisticsConfiguration !== undefined
    const graphDialScaleMaxDefault: string = selectedStatisticsConfiguration?.graphDialScaleMaxDefault || EMPTY_STRING
    const reverseGaugeFace: boolean = selectedStatisticsConfiguration?.isGraphDialFacadeReversed || false
    const reverseGaugeDirection: boolean = selectedStatisticsConfiguration?.isGraphDialAxisReversed || false
    const displayTitle: boolean = selectedStatisticsConfiguration?.isTitleDisplayed || false
    const displayAs: number = selectedStatisticsConfiguration
        ? selectedStatisticsConfiguration.displayedAs
        : TileDisplayAsValues.SIMPLE_TEXT_VALUE
    // eslint-disable-next-line
    const isDisplayedAsUserContent = com.Akixi.RptUtils.IsReportStatDisplayedAsUserContent(
        selectedStatisticsConfiguration
            ? selectedStatisticsConfiguration.displayedAs
            : TileDisplayAsValues.SIMPLE_TEXT_VALUE
    )
    const tileBackgroundColor: number = selectedStatisticsConfiguration
        ? selectedStatisticsConfiguration.colourBackground
        : 20
    const tileTextColor: number = selectedStatisticsConfiguration ? selectedStatisticsConfiguration.colourText : 10
    const imageURL: string = selectedStatisticsConfiguration
        ? selectedStatisticsConfiguration.userContentFilePath
        : EMPTY_STRING
    const fitImage: boolean = selectedStatisticsConfiguration
        ? selectedStatisticsConfiguration.userContentFitTile
        : true

    const isDisplayAsRendered: boolean =
        !isDisplayedAsUserContent &&
        selectedStatisticsConfiguration?.unitType !== STATISTICS_UNIT_TYPE_DATE &&
        selectedStatisticsConfiguration?.unitType !== STATISTICS_UNIT_TYPE_DATETIME &&
        selectedStatisticsConfiguration?.unitType !== STATISTICS_UNIT_TYPE_ICON &&
        selectedStatisticsConfiguration?.unitType !== STATISTICS_UNIT_TYPE_TEXT &&
        selectedStatisticsConfiguration?.unitType !== STATISTICS_UNIT_TYPE_TEXT_TRUNCATABLE &&
        selectedStatisticsConfiguration?.unitType !== STATISTICS_UNIT_TYPE_TIME

    const [tilePreviewProps, setTilePreviewProps]: ReactState<TilePreviewProps> = useState<TilePreviewProps>({
        backgroundColour: 0,
        textColour: 0,
        tileDisplayedAs: 0,
        isGraphDialAxisReversed: false,
        isGraphDialFacadeReversed: false,
        graphDialScaleRangeMax: 100,
        displayTitle,
        title: EMPTY_STRING,
        statID: 0,
        currentValueNormalised: 0,
        currentValueText: EMPTY_STRING,
        unitType: STATISTICS_UNIT_TYPE_UNDEFINED
    })

    const configureExampleTileData = useCallback(
        (currentlySelectedStatisticsConfiguration: StatisticsConfiguration): void => {
            let sampleValue: number = 0
            let displayValue: string = EMPTY_STRING
            const graphDialScaleRangeMax: number = 10

            // Prepare sample values and their string representation different value types e.g. currency, duration, percentage...
            switch (selectedStatisticsConfiguration?.unitType) {
                case STATISTICS_UNIT_TYPE_INTEGERORLONG:
                    sampleValue = Math.floor(graphDialScaleRangeMax * 0.75)
                    displayValue = `${sampleValue}`
                    break
                case STATISTICS_UNIT_TYPE_FLOATORDOUBLE:
                    sampleValue = graphDialScaleRangeMax * 0.75
                    displayValue = `${sampleValue}`
                    break
                case STATISTICS_UNIT_TYPE_PERCENTAGE:
                    sampleValue = graphDialScaleRangeMax * 0.75
                    displayValue = `${sampleValue}%`
                    break
                case STATISTICS_UNIT_TYPE_DURATION: // deliberate fall-through
                    sampleValue = 7
                    displayValue = convertDateToDurationTimeString(new Date('1970-01-01T00:00:00.000'))
                    break
                case STATISTICS_UNIT_TYPE_TIME:
                    // We have to compare statistics ID since there's no field that would distinguish 12h/24h formats.
                    if (selectedStatisticsConfiguration?.ID === 7000) {
                        // This is the only 12h format time statistics.
                        displayValue = convertDateToDurationTimeString12HourFormat(new Date())
                    } else {
                        displayValue = convertDateToDurationTimeString(new Date())
                    }
                    break
                case STATISTICS_UNIT_TYPE_DATE:
                    displayValue = convertDateToDateString(new Date(), language)
                    break
                case STATISTICS_UNIT_TYPE_DATETIME:
                    // We have to compare statistics ID since there's no field that would distinguish 12h/24h formats.
                    if (selectedStatisticsConfiguration?.ID === 7003) {
                        // This is the only 12h format date/time statistics.
                        displayValue = convertDateToDurationDateTimeString12HourFormat(new Date(), language)
                    } else {
                        displayValue = convertDateToDurationDateTimeString(new Date(), language)
                    }
                    break
                case STATISTICS_UNIT_TYPE_TEXT:
                case STATISTICS_UNIT_TYPE_TEXT_TRUNCATABLE:
                    displayValue = 'Note'
                    break
                case STATISTICS_UNIT_TYPE_CURRENCY:
                    sampleValue = graphDialScaleRangeMax * 0.75
                    displayValue = currencyFormatter.Format(sampleValue)
                    break
                case STATISTICS_UNIT_TYPE_ICON: // Fall through, since there's nothing to render here
                default:
                    displayValue = EMPTY_STRING
            }

            const newTilePreviewProps: TilePreviewProps = {
                backgroundColour: currentlySelectedStatisticsConfiguration.colourBackground,
                textColour: currentlySelectedStatisticsConfiguration.colourText,
                tileDisplayedAs: currentlySelectedStatisticsConfiguration.displayedAs,
                graphDialScaleRangeMax,
                isGraphDialAxisReversed: currentlySelectedStatisticsConfiguration.isGraphDialAxisReversed,
                isGraphDialFacadeReversed: currentlySelectedStatisticsConfiguration.isGraphDialFacadeReversed,
                displayTitle,
                title: currentlySelectedStatisticsConfiguration.customName
                    ? currentlySelectedStatisticsConfiguration.customName
                    : currentlySelectedStatisticsConfiguration.mediumName,
                statID: currentlySelectedStatisticsConfiguration.ID,
                currentValueNormalised: sampleValue,
                currentValueText: displayValue,
                imageURL,
                fitImage,
                unitType: selectedStatisticsConfiguration?.unitType || STATISTICS_UNIT_TYPE_UNDEFINED
            }

            setTilePreviewProps(newTilePreviewProps)
        },
        [
            currencyFormatter,
            displayTitle,
            imageURL,
            selectedStatisticsConfiguration,
            setTilePreviewProps,
            fitImage,
            language
        ]
    )

    useEffect((): void => {
        if (selectedStatisticsConfiguration) configureExampleTileData(selectedStatisticsConfiguration)
    }, [selectedStatisticsConfiguration])

    const setDisplayAs = useCallback(
        (value: number): void => {
            if (selectedStatisticsConfiguration) {
                updateSelectedStatisticsConfiguration({
                    ...selectedStatisticsConfiguration,
                    displayedAs: value
                })
            }
        },
        [selectedStatisticsConfiguration, updateSelectedStatisticsConfiguration]
    )

    const setReverseGaugeFace = useCallback(
        (value: boolean): void => {
            if (selectedStatisticsConfiguration) {
                updateSelectedStatisticsConfiguration({
                    ...selectedStatisticsConfiguration,
                    isGraphDialFacadeReversed: value
                })
            }
        },
        [selectedStatisticsConfiguration, updateSelectedStatisticsConfiguration]
    )

    const setReverseGaugeDirection = useCallback(
        (value: boolean): void => {
            if (selectedStatisticsConfiguration) {
                updateSelectedStatisticsConfiguration({
                    ...selectedStatisticsConfiguration,
                    isGraphDialAxisReversed: value
                })
            }
        },
        [selectedStatisticsConfiguration, updateSelectedStatisticsConfiguration]
    )

    const setTileBackgroundColor = useCallback(
        (value: number): void => {
            if (selectedStatisticsConfiguration) {
                updateSelectedStatisticsConfiguration({
                    ...selectedStatisticsConfiguration,
                    colourBackground: value
                })
            }
        },
        [selectedStatisticsConfiguration, updateSelectedStatisticsConfiguration]
    )

    const setTileTextColor = useCallback(
        (value: number): void => {
            if (selectedStatisticsConfiguration) {
                updateSelectedStatisticsConfiguration({
                    ...selectedStatisticsConfiguration,
                    colourText: value
                })
            }
        },
        [selectedStatisticsConfiguration, updateSelectedStatisticsConfiguration]
    )

    const setDisplayTitle = useCallback(
        (value: boolean): void => {
            if (selectedStatisticsConfiguration) {
                updateSelectedStatisticsConfiguration({
                    ...selectedStatisticsConfiguration,
                    isTitleDisplayed: value
                })
            }
        },
        [selectedStatisticsConfiguration, updateSelectedStatisticsConfiguration]
    )

    const setGraphDialScaleMaxDefault = useCallback(
        (value: string): void => {
            if (selectedStatisticsConfiguration) {
                updateSelectedStatisticsConfiguration({
                    ...selectedStatisticsConfiguration,
                    graphDialScaleMaxDefault: value
                })
            }
        },
        [selectedStatisticsConfiguration, updateSelectedStatisticsConfiguration]
    )

    const setImageUrl = useCallback(
        (value: string): void => {
            if (selectedStatisticsConfiguration) {
                updateSelectedStatisticsConfiguration({
                    ...selectedStatisticsConfiguration,
                    userContentFilePath: value
                })
            }
        },
        [selectedStatisticsConfiguration, updateSelectedStatisticsConfiguration]
    )

    const setFitImage = useCallback(
        (value: boolean): void => {
            if (selectedStatisticsConfiguration) {
                updateSelectedStatisticsConfiguration({
                    ...selectedStatisticsConfiguration,
                    userContentFitTile: value
                })
            }
        },
        [selectedStatisticsConfiguration, updateSelectedStatisticsConfiguration]
    )

    return (
        <Fieldset className='display-settings'>
            <Heading level={HeadingLevel.TWO}>
                {language?.get(DisplaySettingsLanguage.DisplaySettings_SectionHeader)}
            </Heading>
            {isDisplayAsRendered ? (
                <FormRow className='display-as'>
                    <Select
                        label={language?.get(DisplaySettingsLanguage.DisplayAsLabel)}
                        options={displayAsOptions}
                        value={displayAs}
                        handleChange={setDisplayAs}
                        disabled={!isStatisticsSelected}
                    />
                </FormRow>
            ) : (
                <></>
            )}

            {isDisplayedAsUserContent ? (
                <FormRow className='image-url'>
                    <TextInput
                        value={imageURL}
                        onChange={setImageUrl}
                        placeholder={language?.get(DisplaySettingsLanguage.ImageUrlPlaceholder)}
                        labelLeft={language?.get(DisplaySettingsLanguage.ImageUrlLabel)}
                        disabled={!isStatisticsSelected}
                    />
                </FormRow>
            ) : (
                <></>
            )}

            {!isDisplayedAsUserContent && displayAs !== TileDisplayAsValues.SIMPLE_TEXT_VALUE ? (
                <>
                    <FormRow className='max-scale'>
                        <TextInput
                            value={graphDialScaleMaxDefault}
                            onChange={setGraphDialScaleMaxDefault}
                            placeholder={language?.get(DisplaySettingsLanguage.AutoValuePlaceholder)}
                            labelLeft={language?.get(DisplaySettingsLanguage.MaxScaleLabel)}
                            disabled={!isStatisticsSelected}
                        />
                    </FormRow>
                    <FormRow isInline className='reverse-gauge'>
                        <Checkbox
                            isChecked={reverseGaugeFace}
                            onChange={setReverseGaugeFace}
                            labelLeft={language?.get(DisplaySettingsLanguage.GaugeFaceLabel)}
                            disabled={!isStatisticsSelected}
                        />
                        <Checkbox
                            isChecked={reverseGaugeDirection}
                            onChange={setReverseGaugeDirection}
                            labelLeft={language?.get(DisplaySettingsLanguage.GaugeFaceDirection)}
                            disabled={!isStatisticsSelected}
                        />
                    </FormRow>
                </>
            ) : (
                <></>
            )}

            <FormRow className='tile-background-color'>
                <Select
                    label={language?.get(DisplaySettingsLanguage.ColorTileBackgroundLabel)}
                    options={colorOptions}
                    value={tileBackgroundColor}
                    handleChange={setTileBackgroundColor}
                    disabled={!isStatisticsSelected}
                />
            </FormRow>
            <FormRow className='fit-image'>
                {isDisplayedAsUserContent ? (
                    <Checkbox
                        onChange={setFitImage}
                        isChecked={fitImage}
                        labelLeft={language?.get(DisplaySettingsLanguage.FitImageLabel)}
                        disabled={!isStatisticsSelected}
                    />
                ) : (
                    <Select
                        label={language?.get(DisplaySettingsLanguage.ColorTileTextLabel)}
                        options={colorOptions}
                        value={tileTextColor}
                        handleChange={setTileTextColor}
                        disabled={!isStatisticsSelected}
                    />
                )}
            </FormRow>

            {!isDisplayedAsUserContent ? (
                <FormRow className='display-title'>
                    <Checkbox
                        onChange={setDisplayTitle}
                        isChecked={displayTitle}
                        labelLeft={language?.get(DisplaySettingsLanguage.DisplayTitleLabel)}
                        disabled={!isStatisticsSelected}
                    />
                </FormRow>
            ) : (
                <></>
            )}

            {selectedStatisticsConfiguration ? (
                <FormRow className='display-preview'>
                    <figure className='display-tile-preview'>
                        <figcaption className='label'>
                            {language?.get(DisplaySettingsLanguage.DisplayPreviewCaption)}
                        </figcaption>
                        <div className='grid'>
                            <TilePreview {...tilePreviewProps} />
                        </div>
                    </figure>
                </FormRow>
            ) : (
                <></>
            )}
        </Fieldset>
    )
}

export default DisplaySettings
